"use client";

import { useEffect, useRef } from "react";

// Flowing wave field + orbiting node — technical, minimal hero animation.
export default function HeroCanvas() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let W = 0;
    let H = 0;
    let dpr = 1;
    let raf = 0;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let mx = 0;
    let my = 0;
    let targetMx = 0;
    let targetMy = 0;

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = entry.contentRect.width;
      H = entry.contentRect.height;

      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);

      // Initialize mouse target center once we have dimensions
      if (targetMx === 0 && targetMy === 0) {
        mx = W / 2;
        my = H / 2;
        targetMx = W / 2;
        targetMy = H / 2;
      }
      // reduced motion: render a single static frame at the new size
      if (reduceMotion) raf = requestAnimationFrame(loop);
    });
    ro.observe(canvas);

    const onMove = (e: MouseEvent) => {
      targetMx = e.offsetX;
      targetMy = e.offsetY;
    };
    const onLeave = () => {
      targetMx = W / 2;
      targetMy = H / 2;
    };
    canvas.addEventListener("mousemove", onMove, { passive: true });
    canvas.addEventListener("mouseleave", onLeave, { passive: true });

    const colors = () => {
      const dark = document.documentElement.getAttribute("data-theme") === "dark";
      return {
        a: dark ? "106,141,255" : "43,95,255",
        fg: dark ? "243,243,240" : "10,10,11",
      };
    };

    function loop(t: number) {
      const c = colors();
      ctx!.clearRect(0, 0, W, H);
      mx += (targetMx - mx) * 0.06;
      my += (targetMy - my) * 0.06;
      const tt = t * 0.001;

      // faint grid
      ctx!.strokeStyle = `rgba(${c.fg},0.05)`;
      ctx!.lineWidth = 1;
      const step = W / 10;
      for (let x = step; x < W; x += step) {
        ctx!.beginPath();
        ctx!.moveTo(x, 0);
        ctx!.lineTo(x, H);
        ctx!.stroke();
      }
      for (let y = step; y < H; y += step) {
        ctx!.beginPath();
        ctx!.moveTo(0, y);
        ctx!.lineTo(W, y);
        ctx!.stroke();
      }

      // flowing wave field
      const lines = 14;
      for (let i = 0; i < lines; i++) {
        const yBase = H * 0.5 + (i - lines / 2) * 14;
        const amp = 18 + i * 1.2;
        const freq = 0.012 + i * 0.0004;
        const phase = tt * (0.6 + i * 0.04) + i * 0.3;
        const dy = (my - H / 2) * 0.15;
        const alpha = 0.08 + Math.abs(Math.sin(tt * 0.5 + i * 0.4)) * 0.18;
        ctx!.strokeStyle = `rgba(${c.a},${alpha})`;
        ctx!.lineWidth = i === 7 ? 1.6 : 0.9;
        ctx!.beginPath();
        for (let x = 0; x <= W; x += 3) {
          const cursorPull = Math.exp(-Math.pow((x - mx) / (W * 0.25), 2)) * 30;
          const y =
            yBase +
            dy +
            Math.sin(x * freq + phase) * amp +
            Math.sin(x * freq * 2.3 + phase * 1.4) * amp * 0.35 +
            cursorPull * Math.sin(tt * 2 + i);
          if (x === 0) ctx!.moveTo(x, y);
          else ctx!.lineTo(x, y);
        }
        ctx!.stroke();
      }

      // orbiting node
      const cx = W / 2;
      const cy = H / 2;
      const orbR = Math.min(W, H) * 0.28;
      const oa = tt * 0.6;
      const ox = cx + Math.cos(oa) * orbR;
      const oy = cy + Math.sin(oa) * orbR * 0.6;

      // connection line
      ctx!.strokeStyle = `rgba(${c.a},0.5)`;
      ctx!.lineWidth = 1;
      ctx!.setLineDash([3, 5]);
      ctx!.beginPath();
      ctx!.moveTo(cx, cy);
      ctx!.lineTo(ox, oy);
      ctx!.stroke();
      ctx!.setLineDash([]);

      // center node
      ctx!.fillStyle = `rgba(${c.fg},0.9)`;
      ctx!.beginPath();
      ctx!.arc(cx, cy, 3, 0, Math.PI * 2);
      ctx!.fill();
      ctx!.strokeStyle = `rgba(${c.fg},0.2)`;
      ctx!.lineWidth = 1;
      ctx!.beginPath();
      ctx!.arc(cx, cy, 12, 0, Math.PI * 2);
      ctx!.stroke();

      // orbit node
      const pulse = 1 + Math.sin(tt * 3) * 0.15;
      ctx!.fillStyle = `rgba(${c.a},1)`;
      ctx!.beginPath();
      ctx!.arc(ox, oy, 5 * pulse, 0, Math.PI * 2);
      ctx!.fill();
      ctx!.strokeStyle = `rgba(${c.a},0.3)`;
      ctx!.beginPath();
      ctx!.arc(ox, oy, 14 * pulse, 0, Math.PI * 2);
      ctx!.stroke();

      // orbit path
      ctx!.strokeStyle = `rgba(${c.a},0.1)`;
      ctx!.beginPath();
      ctx!.ellipse(cx, cy, orbR, orbR * 0.6, 0, 0, Math.PI * 2);
      ctx!.stroke();

      if (!reduceMotion) raf = requestAnimationFrame(loop);
    }
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      canvas.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return <canvas ref={ref} id="heroCanvas" />;
}
